import React, { useEffect, useState, useRef } from 'react';

import { whenAvailable, isFeatureEnabled } from '@vp/ab-reader';
import { usePersonalizationWidgetData } from '@vp/design-personalization-widget';
import { FetchClientExecutor } from '@vp/fetch-client';
import { ChangeSource, OptionsChangedV1, PdpPageEvents } from '@vp/pdp-events';
import { useCustomLogger } from '@vp/pdp-ubik-logging';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { useAuthContext } from '../../../contexts/AuthProvider';
import { ICombination, ICurrentDocument, ProductMedias } from '../../../types/interfaces';
import { CarouselData } from '../domain/CarouselData';
import { Combination } from '../domain/Combination';

import { Carousel } from './Carousel';
import { buildHeroCarouselMedias } from './imageTransformations/buildHeroCarouselImages';

const pdpSceneImprovements = 'pdp_initial_scene_purpose';
export interface PDPHeroCarouselProps {
  tenant: string;
  selectedCombination: ICombination;
  zoomContainerId?: string;
  canonicalDomain: string;
  mpvId: string;
  currentDocument?: ICurrentDocument;
  locale: string;
  productKey: string;
  productVersion: number;
  medias: ProductMedias;
  showBrandKit: boolean;
  onBrandkitApiError?: (hasError: boolean) => void;
  designPersonalizationContextIdOverride?: string;
}

export const PDPHeroCarousel: React.FC<PDPHeroCarouselProps> = (props) => {
  const { t } = useLocalizations();
  const { logger } = useCustomLogger();
  const { isInitialized, authorizationHeader } = useAuthContext();

  const [heroCarouselData, setHeroCarouselData] = useState(
    CarouselData.from(buildHeroCarouselMedias(props.medias))
  );
  const [combination, setCombination] = useState<Combination>(
    new Combination(props.selectedCombination, true)
  );
  const [currentDocument, setCurrentDocument] = useState<ICurrentDocument | undefined>(
    props.currentDocument
      ? {
          documentUri: props.currentDocument.documentUri,
          altText: t('shop-with-your-design-alt-text'),
        }
      : undefined
  );
  const [isABReaderAvailable, setIsABReaderAvailable] = useState<boolean | undefined>(false);
  const [isOverrideInitialized, setIsOverrideInitialized] = useState(false);

  const {
    designPersonalizationContextId,
    isPersonalizationWidgetUpdating,
    designPersonalizationError,
    setDesignPersonalizationContextEnabled,
  } = props.showBrandKit
    ? usePersonalizationWidgetData()
    : {
        designPersonalizationContextId: undefined,
        isPersonalizationWidgetUpdating: false,
        designPersonalizationError: null,
        setDesignPersonalizationContextEnabled: () => {},
      };

  useEffect(() => {
    if (props.designPersonalizationContextIdOverride && !isOverrideInitialized) {
      if (!designPersonalizationContextId) {
        setDesignPersonalizationContextEnabled(true);
      }
      if (designPersonalizationContextId) {
        setIsOverrideInitialized(true);
        if (props.designPersonalizationContextIdOverride !== designPersonalizationContextId) {
          setDesignPersonalizationContextEnabled(false);
        }
      }
    }
  }, [
    props.designPersonalizationContextIdOverride,
    designPersonalizationContextId,
    isOverrideInitialized,
    setDesignPersonalizationContextEnabled,
  ]);

  const shouldUseBrandkit = props.showBrandKit && !designPersonalizationError;

  const combinationRef = useRef(combination);
  const currentDocumentRef = useRef(currentDocument);
  const designPersonalizationContextIdRef = useRef(designPersonalizationContextId);
  const shouldUseBrandkitRef = useRef(shouldUseBrandkit);

  const updateBrandkitPreview = async (
    combination: Combination,
    designPersonalizationContextId: string | undefined
  ) => {
    const isRemoved = heroCarouselData.removeBrandkitImage();
    if (designPersonalizationContextId) {
      const result = await heroCarouselData.addBrandkitImage(
        props.locale,
        props.productKey,
        props.productVersion,
        designPersonalizationContextId!,
        combination,
        t('brandkit-image-alt-text'),
        FetchClientExecutor.client
      );
      if (result) {
        setHeroCarouselData(heroCarouselData.copy());
        props.onBrandkitApiError?.(false);
        PdpPageEvents.sendDesignPersonalizationContextIdUpdatedEventV1({
          designPersonalizationContextId: designPersonalizationContextId,
        });
      } else {
        PdpPageEvents.sendDesignPersonalizationContextIdUpdatedEventV1({
          designPersonalizationContextId: null,
        });
        props.onBrandkitApiError?.(true);
        logger.error('Failed to add brandkit preview image to hero carousel');
      }
    } else {
      if (isRemoved) {
        setHeroCarouselData(heroCarouselData.copy());
        PdpPageEvents.sendDesignPersonalizationContextIdUpdatedEventV1({
          designPersonalizationContextId: null,
        });
      }
    }
  };

  useEffect(() => {
    if (!isPersonalizationWidgetUpdating && shouldUseBrandkit) {
      updateBrandkitPreview(combination, designPersonalizationContextId);
    }
  }, [designPersonalizationContextId, isPersonalizationWidgetUpdating, shouldUseBrandkit]);

  const calculateDocumentImages = async (
    currentDocument: ICurrentDocument | undefined,
    combination: Combination,
    isSceneImprovementsEnabled: boolean
  ) => {
    if (!currentDocument) return;
    if (!isInitialized) return;

    const result = await heroCarouselData.addDocumentImage(
      currentDocument!,
      props.tenant,
      props.locale,
      props.productKey,
      props.productVersion,
      combination,
      isSceneImprovementsEnabled,
      logger,
      { Authorization: authorizationHeader! },
      FetchClientExecutor.client
    );

    if (!result) {
      logger.error('Failed to transform template into document');
      setCurrentDocument(undefined);
      PdpPageEvents.sendCurrentDocumentInvalidatedEventV1({
        reason: 'ErrorGeneratingScenes',
      });
    } else {
      setHeroCarouselData(heroCarouselData.copy());
    }
  };

  useEffect(() => {
    combinationRef.current = combination;
  }, [combination]);

  useEffect(() => {
    currentDocumentRef.current = currentDocument;
  }, [currentDocument]);

  useEffect(() => {
    designPersonalizationContextIdRef.current = designPersonalizationContextId;
  }, [designPersonalizationContextId]);

  useEffect(() => {
    shouldUseBrandkitRef.current = shouldUseBrandkit;
  }, [shouldUseBrandkit]);

  useEffect(() => {
    const handleOptionsChanged = async (optionsChanged: OptionsChangedV1) => {
      const prevCurrentDocument = currentDocumentRef.current;

      const designPersonalizationContextId = designPersonalizationContextIdRef.current;
      const newCombination = new Combination(
        optionsChanged.combination,
        optionsChanged.source !== ChangeSource.USER
      );
      const isSceneImprovementsEnabled =
        isABReaderAvailable === true ? isFeatureEnabled(pdpSceneImprovements) : false;
      await calculateDocumentImages(
        prevCurrentDocument,
        newCombination,
        isSceneImprovementsEnabled!
      );
      if (designPersonalizationContextId && shouldUseBrandkitRef.current)
        await updateBrandkitPreview(newCombination, designPersonalizationContextId);

      setCombination((prevCombination) => {
        return newCombination.equals(prevCombination) ? prevCombination : newCombination;
      });
    };
    return PdpPageEvents.listenToOptionsChangedEventV1(handleOptionsChanged);
  }, [isInitialized, isABReaderAvailable]);

  useEffect(() => {
    whenAvailable((available) => {
      setIsABReaderAvailable(available);
    });
  }, []);

  useEffect(() => {
    const handleDocumentChange = async () => {
      if (
        currentDocument &&
        currentDocument.documentUri &&
        props.productKey &&
        props.productVersion &&
        isABReaderAvailable !== false
      ) {
        const isSceneImprovementsEnabled =
          isABReaderAvailable === true ? isFeatureEnabled(pdpSceneImprovements) : false;
        await calculateDocumentImages(currentDocument, combination, isSceneImprovementsEnabled);
      } else {
        const isRemoved = heroCarouselData.removeDocumentImage();
        if (isRemoved) setHeroCarouselData(heroCarouselData.copy());
      }
    };
    handleDocumentChange();
  }, [currentDocument, isInitialized, isABReaderAvailable]);

  return (
    <Carousel
      tenant={props.tenant}
      carouselData={heroCarouselData}
      selectedCombination={combination ?? heroCarouselData.getFirstCombination()}
      zoomContainerId={props.zoomContainerId}
      canonicalDomain={props.canonicalDomain}
      mpvId={props.mpvId}
      productKey={props.productKey}
    />
  );
};
